import axios, {
  AxiosHeaders,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { IOptions } from "../api-sdk";

class BaseService {
  protected client: AxiosInstance;

  constructor(options: IOptions) {
    this.client = axios.create({
      baseURL: options.baseUrl,
      withCredentials: options?.withCredentials || false,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...(options.headers || {}),
      },
    });

    // Register *before* user request interceptors. Axios defaults
    // `legacyInterceptorReqResOrdering: true`, so request interceptors run in
    // reverse registration order — this handler must run last so it runs after
    // user hooks and clears `Content-Type` for `FormData` (instance default is
    // `application/json`).
    this.client.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        if (
          typeof FormData !== "undefined" &&
          config.data instanceof FormData
        ) {
          const headers = AxiosHeaders.from(config.headers);
          headers.delete("Content-Type");
          config.headers = headers;
        }
        return config;
      },
    );

    // Attach user-provided interceptors
    if (options.interceptors?.request) {
      this.client.interceptors.request.use(
        (config) =>
          options.interceptors!.request!(config as AxiosRequestConfig) as any
      );
    }

    if (options.interceptors?.response || options.interceptors?.responseError) {
      this.client.interceptors.response.use(
        options.interceptors.response || ((r) => r),
        options.interceptors.responseError || ((e) => Promise.reject(e))
      );
    }
  }

  /**
   * Performs an HTTP request with the same Axios client as the rest of the service
   * (`baseURL` from {@link IOptions}, default headers, credentials, interceptors).
   *
   * Use this when the SDK does not yet expose a dedicated method for an endpoint.
   * Any service instance works (for example `zott.settings.request(...)`); configuration is identical across services.
   *
   * @typeParam T - Expected shape of `response.data`.
   * @param config - Axios request config (`method`, `url`, `params`, `data`, `headers`, etc.).
   */
  public async request<T = unknown>(
    config: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    try {
      return await this.client.request<T>(config);
    } catch (error) {
      throw error;
    }
  }
}

export default BaseService;
